import React, { useEffect, useState } from 'react';
import { bookingsAPI } from './services/api';
import SalesAgreement from './SalesAgreement';
import './PaymentPage.css';

interface BookingData {
  id: number;
  reservation_id: string;
  customer_name: string;
  customer_email: string;
  customer_phone?: string;
  product_type: string;
  total_price: number;
  status: string;
  payment_status: string;
  room_name?: string;
  breakfast_total?: number;
  dinner_total?: number;
  grand_total?: number;
  items?: {
    roomTypeName?: string;
    roomTypeId?: string;
  };
  customer_info?: {
    checkIn?: string;
    checkOut?: string;
    adults?: number;
    children?: number;
    nights?: number;
    paymentToken?: string;
    includeBreakfast?: boolean;
    includeDinner?: boolean;
    breakfastCost?: number;
    dinnerCost?: number;
  };
  meal_bookings?: Array<{
    id: number;
    product_type: string;
    total_price: number;
    booking_date: string;
  }>;
  created_at?: string;
}

interface PaymentPageProps {
  reservationId: string;
}

const PaymentPage: React.FC<PaymentPageProps> = ({ reservationId }) => {
  const [booking, setBooking] = useState<BookingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [agreementAccepted, setAgreementAccepted] = useState(false);
  const [showPayment, setShowPayment] = useState(false);

  useEffect(() => {
    fetchBooking();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reservationId]);

  // NOT: PENDING oluşturma PaymentSuccess.tsx'de yapılıyor
  // Burada erken oluşturmak başarısız ödeme sonrası bloke riski yaratır

  const fetchBooking = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await bookingsAPI.getByReservationId(reservationId);
      if (response.data.success && response.data.data) {
        const data = response.data.data;
        // Check if already paid
        if (data.payment_status === 'paid' || data.status === 'confirmed') {
          setError('Bu rezervasyon için ödeme zaten yapılmıştır.');
          setBooking(data);
        } else if (data.status === 'cancelled') {
          setError('Bu rezervasyon iptal edilmiştir.');
          setBooking(data);
        } else {
          setBooking(data);
        }
      } else {
        setError('Rezervasyon bulunamadı.');
      }
    } catch (err: any) {
      console.error('Booking fetch error:', err);
      if (err.response?.status === 404) {
        setError('Rezervasyon bulunamadı. Lütfen bağlantıyı kontrol edin.');
      } else {
        setError('Bir hata oluştu. Lütfen daha sonra tekrar deneyin.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleProceedToPayment = () => {
    if (!agreementAccepted) {
      alert('Lütfen satış sözleşmesini okuyup onaylayın.');
      return;
    }
    setShowPayment(true);
  };

  const goToHome = () => {
    window.location.href = '/';
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' });
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  // Loading state
  if (loading) {
    return (
      <div className="payment-page">
        <div className="payment-page-container">
          <div className="payment-page-loading">
            <div className="loading-spinner"></div>
            <p>Rezervasyon bilgileri yükleniyor...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error - no booking found
  if (error && !booking) {
    return (
      <div className="payment-page">
        <div className="payment-page-container">
          <div className="payment-page-error">
            <div className="error-icon">❌</div>
            <h2>Hata</h2>
            <p>{error}</p>
            <button className="btn-home" onClick={goToHome}>
              Ana Sayfaya Dön
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Already paid or cancelled
  if (error && booking) {
    return (
      <div className="payment-page">
        <div className="payment-page-container">
          <div className="payment-page-info">
            <div className="info-icon">
              {booking.payment_status === 'paid' ? '✅' : '⚠️'}
            </div>
            <h2>
              {booking.payment_status === 'paid' ? 'Ödeme Tamamlandı' : 'Rezervasyon İptal'}
            </h2>
            <p>{error}</p>
            <div className="booking-summary-card">
              <div className="summary-row">
                <span>Rezervasyon No:</span>
                <strong>{booking.reservation_id}</strong>
              </div>
              <div className="summary-row">
                <span>Misafir:</span>
                <strong>{booking.customer_name}</strong>
              </div>
            </div>
            <button className="btn-home" onClick={goToHome}>
              Ana Sayfaya Dön
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!booking) return null;

  const paymentToken = booking.customer_info?.paymentToken;
  const iframeUrl = paymentToken
    ? `https://www.paytr.com/odeme/guvenli/${paymentToken}`
    : '';

  const checkIn = booking.customer_info?.checkIn;
  const checkOut = booking.customer_info?.checkOut;
  const adults = booking.customer_info?.adults || 0;
  const children = booking.customer_info?.children || 0;
  const nights = booking.customer_info?.nights || 0;
  const roomName = booking.room_name || booking.items?.roomTypeName || 'Oda';
  
  // Yemek maliyetleri: önce linked meal bookings, yoksa customer_info'dan al
  const breakfastCost = (booking.breakfast_total && booking.breakfast_total > 0)
    ? booking.breakfast_total
    : (booking.customer_info?.breakfastCost || 0);
  const dinnerCost = (booking.dinner_total && booking.dinner_total > 0)
    ? booking.dinner_total
    : (booking.customer_info?.dinnerCost || 0);
  const roomPrice = parseFloat(String(booking.total_price));
  const grandTotal = roomPrice + breakfastCost + dinnerCost;

  return (
    <div className="payment-page">
      <div className="payment-page-container">
        {/* Header */}
        <div className="payment-page-header">
          <img 
            src="/logo.png" 
            alt="Villa Polikne" 
            className="payment-logo"
            onClick={goToHome}
            style={{ cursor: 'pointer' }}
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
          />
          <h1>💳 Ödeme Sayfası</h1>
          <p className="reservation-badge">Rezervasyon: {booking.reservation_id}</p>
        </div>

        {!showPayment ? (
          <>
            {/* Booking Summary */}
            <div className="booking-details-card">
              <h2>📋 Rezervasyon Özeti</h2>
              
              <div className="detail-section">
                <h3>🏨 Konaklama Bilgileri</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Oda</span>
                    <span className="detail-value">{roomName}</span>
                  </div>
                  {checkIn && (
                    <div className="detail-item">
                      <span className="detail-label">Giriş</span>
                      <span className="detail-value">{formatDate(checkIn)}</span>
                    </div>
                  )}
                  {checkOut && (
                    <div className="detail-item">
                      <span className="detail-label">Çıkış</span>
                      <span className="detail-value">{formatDate(checkOut)}</span>
                    </div>
                  )}
                  <div className="detail-item">
                    <span className="detail-label">Süre</span>
                    <span className="detail-value">{nights} gece</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Misafir</span>
                    <span className="detail-value">
                      {adults} yetişkin{children > 0 ? `, ${children} çocuk` : ''}
                    </span>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h3>👤 Misafir Bilgileri</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Ad Soyad</span>
                    <span className="detail-value">{booking.customer_name}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">E-posta</span>
                    <span className="detail-value">{booking.customer_email}</span>
                  </div>
                  {booking.customer_phone && (
                    <div className="detail-item">
                      <span className="detail-label">Telefon</span>
                      <span className="detail-value">{booking.customer_phone}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Price breakdown */}
              <div className="detail-section price-section">
                <h3>💰 Fiyat Detayı</h3>
                <div className="price-breakdown">
                  <div className="price-row">
                    <span>Oda ({nights} gece)</span>
                    <span>{formatPrice(roomPrice)}</span>
                  </div>
                  {breakfastCost > 0 && (
                    <div className="price-row">
                      <span>Kahvaltı</span>
                      <span>{formatPrice(breakfastCost)}</span>
                    </div>
                  )}
                  {dinnerCost > 0 && (
                    <div className="price-row">
                      <span>Akşam Yemeği</span>
                      <span>{formatPrice(dinnerCost)}</span>
                    </div>
                  )}
                  <div className="price-row total">
                    <span>Toplam</span>
                    <span>{formatPrice(grandTotal)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Sales Agreement */}
            <div className="agreement-section">
              <SalesAgreement
                isChecked={agreementAccepted}
                onCheckChange={setAgreementAccepted}
              />
            </div>

            {/* Payment Button */}
            <button
              className={`btn-proceed-payment ${!agreementAccepted ? 'disabled' : ''}`}
              onClick={handleProceedToPayment}
              disabled={!agreementAccepted}
            >
              💳 Ödemeye Geç — {formatPrice(grandTotal)}
            </button>

            {!paymentToken && (
              <div className="payment-page-warning">
                <p>⚠️ Ödeme bağlantısı bulunamadı. Lütfen yönetici ile iletişime geçin.</p>
              </div>
            )}
          </>
        ) : (
          <>
            {/* PayTR iframe */}
            <div className="payment-iframe-section">
              <div className="payment-info-bar">
                <p>🔒 PayTR güvenli ödeme sayfası</p>
                <small>Rezervasyon: {booking.reservation_id} — Tutar: {formatPrice(grandTotal)}</small>
              </div>
              
              {iframeUrl ? (
                <iframe
                  src={iframeUrl}
                  id="paytriframe"
                  frameBorder="0"
                  scrolling="yes"
                  style={{ width: '100%', height: '600px', border: 'none', borderRadius: '8px' }}
                  title="PayTR Payment"
                />
              ) : (
                <div className="payment-page-error">
                  <p>⚠️ Ödeme formu yüklenemedi. Lütfen yönetici ile iletişime geçin.</p>
                </div>
              )}

              <div className="payment-security-note">
                <p>🛡️ Ödeme bilgileriniz PayTR güvencesi altındadır. Kart bilgileriniz tarafımızla paylaşılmaz.</p>
              </div>
            </div>

            <button className="btn-back" onClick={() => setShowPayment(false)}>
              ← Geri Dön
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentPage;
